import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { auth, getErrorMessage } from '../../utils/api';
import { validatePassword, passwordChecklist } from '../../utils/passwordPolicy';
import { toast } from 'sonner';
import { Store, Eye, EyeOff, Heart, Loader2, Check, X } from 'lucide-react';
import Logo from '../Logo';
import api from '../../utils/api';

export default function RetailerAuth({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [charities, setCharities] = useState([]);
  const [loadingCharities, setLoadingCharities] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    charity_id: '',
  });
  const [loading, setLoading] = useState(false);

  // Fetch charities when switching to register tab
  useEffect(() => {
    if (!isLogin && charities.length === 0) {
      fetchCharities();
    }
  }, [isLogin]);

  const fetchCharities = async () => {
    setLoadingCharities(true);
    try {
      const response = await api.get('/charities/public');
      setCharities(response.data);
    } catch (error) {
      console.error('Failed to load charities:', error);
      toast.error('Failed to load charities');
    } finally {
      setLoadingCharities(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await auth.login({
          email: formData.email,
          password: formData.password,
          role: 'DRLP',
        });
        // Don't reset loading on success - let navigation happen
        onLogin(response.data.access_token, response.data.user);
        return; // Exit early, don't hit finally
      } else {
        // Charity is optional — only send it if the user picked one.
        // This unblocks retailer onboarding even when no charities exist yet.
        const policyError = validatePassword(formData.password, { email: formData.email });
        if (policyError) {
          toast.error(policyError);
          setLoading(false);
          return;
        }
        const registerPayload = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: 'DRLP',
        };
        if (formData.charity_id) {
          registerPayload.charity_id = formData.charity_id;
        }

        const response = await auth.register(registerPayload);
        // Don't reset loading on success - let navigation happen
        onLogin(response.data.access_token, response.data.user);
        toast.success('Registration successful! Your account is pending approval.');
        return; // Exit early, don't hit finally
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Authentication failed'));
      setLoading(false); // Only reset loading on error
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      await auth.requestPasswordReset({ email: resetEmail, role: 'DRLP' });
      toast.success('Password reset link sent! Check your email.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset link'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Logo size="large" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-700">
            DealShaq Grocer App
          </CardTitle>
          <CardDescription className="text-base">Manage your sizzling hot deals</CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="retailer-login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="retailer-register-tab">Register</TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4">
              <TabsContent value="login" className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="retailer-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setResetEmail(formData.email);
                        setShowForgotPassword(true);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="retailer-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="retailer-toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                <div>
                  <Label htmlFor="name">Business Name</Label>
                  <Input
                    id="name"
                    data-testid="retailer-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your store name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input
                    id="register-email"
                    data-testid="retailer-register-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="business@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      data-testid="retailer-register-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Pick a strong one"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <ul className="text-xs space-y-1 bg-gray-50 rounded-md p-3 mt-2" data-testid="password-policy-list">
                    {passwordChecklist(formData.password, { email: formData.email }).map((rule) => (
                      <li
                        key={rule.label}
                        className={`flex items-start gap-2 ${rule.ok ? 'text-emerald-700' : 'text-gray-500'}`}
                      >
                        {rule.ok ? (
                          <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        ) : (
                          <X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                        )}
                        <span>{rule.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Charity Selection - Optional */}
                <div>
                  <Label htmlFor="charity" className="flex items-center space-x-2">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <span>Charity Partner <span className="text-gray-400 text-xs font-normal">(optional)</span></span>
                  </Label>
                  <Select
                    value={formData.charity_id}
                    onValueChange={(value) => setFormData({ ...formData, charity_id: value })}
                  >
                    <SelectTrigger 
                      id="charity"
                      data-testid="retailer-charity-select"
                      className="w-full mt-1"
                    >
                      <SelectValue placeholder={loadingCharities ? "Loading charities..." : "Select a charity partner (optional)"} />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingCharities ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          <span className="text-sm text-gray-500">Loading...</span>
                        </div>
                      ) : charities.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">
                          No charities available yet — you can add one later
                        </div>
                      ) : (
                        charities.map((charity) => (
                          <SelectItem key={charity.id} value={charity.id}>
                            <div className="flex flex-col">
                              <span>{charity.name}</span>
                              {charity.category && (
                                <span className="text-xs text-gray-500">{charity.category}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    If selected, 0.45% of each transaction is donated to this charity. You can skip for now and set it later.
                  </p>
                </div>
              </TabsContent>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="retailer-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {isLogin ? 'Logging in...' : 'Registering...'}
                  </>
                ) : (
                  isLogin ? 'Login' : 'Register'
                )}
              </Button>
            </form>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email and we'll send you a password reset link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                data-testid="reset-email-input"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <Button
              onClick={handleForgotPassword}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="send-reset-btn"
            >
              Send Reset Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
